import { useCallback, useEffect, useState } from 'react'
import { useApolloClient } from '@apollo/client'

import { F0Box, F0Button, F0OneIcon, F0Text, F0Heading, useAiChat } from '@factorialco/f0-react'
import { Stack } from '@factorialco/f0-react/dist/experimental'
import {
  TicketingOnboardingStateDocument,
  TicketingTicketCategoriesDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { useTicketingPolicies } from 'modules/ticketing/hooks/useTicketingPolicies'
import {
  trackViewTicketingOnboarding,
  trackClickQuickSetup,
  trackClickSetupWithOne,
} from 'modules/ticketing/lib/tracking'

import { QuickSetupDialog } from './QuickSetupDialog'

export const TicketingOnboarding = () => {
  const client = useApolloClient()
  const { aiEnabled } = useTicketingPolicies()
  const { sendMessage, setOpen: setAiChatOpen } = useAiChat()
  const [showQuickSetup, setShowQuickSetup] = useState(false)

  useEffect(() => {
    trackViewTicketingOnboarding()
  }, [])

  const handleQuickSetupClose = useCallback(() => {
    setShowQuickSetup(false)
  }, [])

  const handleQuickSetupComplete = useCallback(() => {
    void client.refetchQueries({
      include: [TicketingOnboardingStateDocument, TicketingTicketCategoriesDocument],
    })
  }, [client])

  const handleQuickSetup = useCallback(() => {
    trackClickQuickSetup()
    setShowQuickSetup(true)
  }, [])

  const handleSetupWithOne = useCallback(() => {
    trackClickSetupWithOne()
    setAiChatOpen(true)
    sendMessage(i18n.t('ticketing.onboarding.one_prompt'))
  }, [sendMessage, setAiChatOpen])

  return (
    <>
      <F0Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap="lg"
        paddingX="xl"
        paddingY="xl"
        height="full"
      >
        <Stack gap="2" alignItems="center">
          <F0Heading variant="heading" content={i18n.t('ticketing.onboarding.title')} />
          <F0Text
            variant="description"
            color="secondary"
            content={i18n.t('ticketing.onboarding.description')}
          />
        </Stack>
        <F0Box display="flex" flexDirection="row" gap="md">
          <F0Button
            label={i18n.t('ticketing.onboarding.quick_setup')}
            variant="default"
            onClick={handleQuickSetup}
          />
          {aiEnabled && (
            <F0Button
              label={i18n.t('ticketing.onboarding.setup_with_one')}
              variant="outline"
              icon={F0OneIcon}
              onClick={handleSetupWithOne}
            />
          )}
        </F0Box>
      </F0Box>
      {showQuickSetup && (
        <QuickSetupDialog onClose={handleQuickSetupClose} onComplete={handleQuickSetupComplete} />
      )}
    </>
  )
}
